import { useState, useEffect, useRef, useCallback } from 'react';

const INTERVAL = 5000;

const slides = [
  {
    image: '/images/bg01.png',
    title: '專業代理歐美工業控制系統',
    subtitle: 'Industrial Automation.',
  },
  {
    image: '/images/bg02.png',
    title: '防爆設備與安全控制解決方案',
    subtitle: 'Explosion-proof & Safety Solutions.',
  },
  {
    image: '/images/bg03.png',
    title: '歐美品牌授權代理・品質保證',
    subtitle: 'Certified European & American Brands.',
  },
];

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progKey, setProgKey] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = useCallback((idx: number) => {
    setCurrent(idx);
    setProgKey(k => k + 1);
  }, []);

  const prev = useCallback(() => {
    goTo((current - 1 + slides.length) % slides.length);
  }, [current, goTo]);

  const next = useCallback(() => {
    goTo((current + 1) % slides.length);
  }, [current, goTo]);

  // Auto-play
  useEffect(() => {
    if (paused) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      setCurrent(i => (i + 1) % slides.length);
      setProgKey(k => k + 1);
    }, INTERVAL);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [paused, current]);

  return (
    <div
      className="relative w-full h-[420px] md:h-[520px] overflow-hidden bg-gray-900 select-none"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Slides */}
      {slides.map((slide, idx) => (
        <div
          key={idx}
          className={`absolute inset-0 transition-opacity duration-700 ${idx === current ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <div
              className={`bg-black/50 px-8 py-4 inline-block transition-all duration-700
                ${idx === current ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            >
              <h2 className="text-2xl md:text-4xl font-bold text-white tracking-wide mb-2">
                {slide.title}
              </h2>
              <p className="text-lg md:text-2xl text-[#4fc3f7] font-light italic">
                {slide.subtitle}
              </p>
            </div>
          </div>
        </div>
      ))}

      {/* Arrow Left */}
      <button
        onClick={prev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/30 hover:bg-black/60 text-white transition-colors cursor-pointer z-20"
        aria-label="Previous"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>

      {/* Arrow Right */}
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/30 hover:bg-black/60 text-white transition-colors cursor-pointer z-20"
        aria-label="Next"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      {/* Dots + slide counter */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-3 z-20">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goTo(idx)}
            className={`transition-all cursor-pointer rounded-full
              ${idx === current
                ? 'w-6 h-3 bg-[#1a73b5]'
                : 'w-3 h-3 bg-white/50 hover:bg-white/80'}`}
            aria-label={`Slide ${idx + 1}`}
          />
        ))}
      </div>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/20 z-20">
        <div
          key={progKey}
          className={`h-full bg-[#1a73b5] ${paused ? '' : 'animate-progress'}`}
          style={paused ? { animationPlayState: 'paused' } : {}}
        />
      </div>

      {/* Pause indicator */}
      {paused && (
        <div className="absolute top-4 right-4 z-20 flex items-center gap-1.5 bg-black/40 px-2.5 py-1 text-white/70 text-xs">
          <i className="ri-pause-line text-sm" />
          已暫停
        </div>
      )}
    </div>
  );
}
